import {
  App,
  Button,
  Card,
  DatePicker,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import {
  CheckCircleFilled,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import {
  attendanceApi,
  type AttendanceGroup,
  type Holiday,
  type HolidayType,
  type Schedule,
} from '@/api/attendance';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const HOLIDAY_TYPE_LABEL: Record<HolidayType, string> = {
  PUBLIC_HOLIDAY: 'Public holiday',
  SCHOOL_HOLIDAY: 'School holiday',
  ADHOC_OFF: 'Ad-hoc off',
};
const HOLIDAY_TYPE_COLOR: Record<HolidayType, string> = {
  PUBLIC_HOLIDAY: 'red',
  SCHOOL_HOLIDAY: 'blue',
  ADHOC_OFF: 'orange',
};

export function HolidaysPage() {
  return (
    <div style={{ display: 'grid', gap: 22 }}>
      <PageHeader
        eyebrow="Module / Attendance"
        title="Holidays & schedule"
        description="The weekly-off schedule is date-effective; historical attendance always resolves whichever schedule was active that day."
      />
      <ScheduleSection />
      <HolidaysSection />
    </div>
  );
}

/* ---------- schedule section ---------- */

interface ScheduleFormShape {
  attendanceGroup: AttendanceGroup;
  weeklyOffDays: number[];
  effectiveFrom: Dayjs;
}

function ScheduleSection() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canEdit = useAuthStore((s) => s.hasAuthority('ATTENDANCE:EDIT'));

  const [group, setGroup] = useState<AttendanceGroup>('STUDENT');
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm<ScheduleFormShape>();

  const { data, isLoading } = useQuery({
    queryKey: ['attendance', 'schedules', group],
    queryFn: () => attendanceApi.listSchedules(group),
  });

  const createNew = useMutation({
    mutationFn: (values: ScheduleFormShape) =>
      attendanceApi.createSchedule({
        attendanceGroup: values.attendanceGroup,
        weeklyOffDays: [...values.weeklyOffDays].sort((a, b) => a - b),
        effectiveFrom: values.effectiveFrom.format('YYYY-MM-DD'),
      }),
    onSuccess: () => {
      message.success('New schedule opened, prior closed automatically');
      qc.invalidateQueries({ queryKey: ['attendance', 'schedules'] });
      setOpen(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const columns: TableProps<Schedule>['columns'] = [
    {
      title: 'Effective',
      width: 220,
      render: (_, row) => (
        <Space>
          <span>{dayjs(row.effectiveFrom).format('D MMM YYYY')}</span>
          <span style={{ opacity: 0.5 }}>→</span>
          <span>{row.effectiveTo ? dayjs(row.effectiveTo).format('D MMM YYYY') : 'open'}</span>
          {row.current && (
            <Tag color="processing" icon={<CheckCircleFilled />}>
              Current
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Weekly off',
      render: (_, row) =>
        row.weeklyOffDays.length === 0 ? (
          <Typography.Text type="secondary">No weekly off</Typography.Text>
        ) : (
          <Space size={4} wrap>
            {row.weeklyOffDays.map((d) => (
              <Tag key={d}>{WEEKDAY_LABELS[d]}</Tag>
            ))}
          </Space>
        ),
    },
  ];

  return (
    <Card
      title="Weekly schedule"
      extra={
        <Space>
          <Select
            value={group}
            onChange={setGroup}
            options={[
              { value: 'STUDENT', label: 'Student' },
              { value: 'STAFF', label: 'Staff' },
              { value: 'BOTH', label: 'Both' },
            ]}
            style={{ minWidth: 130 }}
          />
          {canEdit && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                form.resetFields();
                form.setFieldsValue({
                  attendanceGroup: group,
                  weeklyOffDays: group === 'STAFF' ? [6] : [5, 6],
                  effectiveFrom: dayjs(),
                });
                setOpen(true);
              }}
            >
              New schedule
            </Button>
          )}
        </Space>
      }
    >
      {data && data.length === 0 && !isLoading ? (
        <Empty description="No schedule defined for this group yet" />
      ) : (
        <Table
          rowKey="id"
          size="middle"
          loading={isLoading}
          columns={columns}
          dataSource={data ?? []}
          pagination={false}
        />
      )}

      <Modal
        title="Open a new schedule"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={createNew.isPending}
        okText="Open"
        destroyOnHidden
      >
        {open && (
          <Form
            form={form}
            layout="vertical"
            onFinish={(values: ScheduleFormShape) => createNew.mutate(values)}
          >
            <Form.Item label="Group" name="attendanceGroup" rules={[{ required: true }]}>
              <Select
                options={[
                  { value: 'STUDENT', label: 'Student' },
                  { value: 'STAFF', label: 'Staff' },
                  { value: 'BOTH', label: 'Both' },
                ]}
              />
            </Form.Item>
            <Form.Item
              label="Effective from"
              name="effectiveFrom"
              rules={[{ required: true }]}
              tooltip="The prior schedule will be closed at the day before this date."
            >
              <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
            </Form.Item>
            <Form.Item
              label="Weekly off days"
              name="weeklyOffDays"
              rules={[{ type: 'array', message: 'Pick zero or more days' }]}
              tooltip="0=Mon, 6=Sun"
            >
              <Select
                mode="multiple"
                options={WEEKDAY_LABELS.map((label, value) => ({ value, label }))}
              />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </Card>
  );
}

/* ---------- holidays section ---------- */

interface HolidayFormShape {
  holidayDate: Dayjs;
  name: string;
  appliesTo: AttendanceGroup;
  holidayType: HolidayType;
}

function HolidaysSection() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('ATTENDANCE:CREATE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('ATTENDANCE:EDIT'));
  const canDelete = useAuthStore((s) => s.hasAuthority('ATTENDANCE:DELETE'));

  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf('month'),
    dayjs().endOf('month').add(2, 'month'),
  ]);
  const [groupFilter, setGroupFilter] = useState<AttendanceGroup | undefined>();
  const [editing, setEditing] = useState<Holiday | null>(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm<HolidayFormShape>();

  const params = {
    from: range[0].format('YYYY-MM-DD'),
    to: range[1].format('YYYY-MM-DD'),
    group: groupFilter,
  };

  const { data, isLoading } = useQuery({
    queryKey: ['attendance', 'holidays', params],
    queryFn: () => attendanceApi.listHolidays(params),
  });

  const upsert = useMutation({
    mutationFn: (values: HolidayFormShape) => {
      const body = {
        holidayDate: values.holidayDate.format('YYYY-MM-DD'),
        name: values.name.trim(),
        appliesTo: values.appliesTo,
        holidayType: values.holidayType,
      };
      return editing
        ? attendanceApi.updateHoliday(editing.id, body)
        : attendanceApi.createHoliday(body);
    },
    onSuccess: () => {
      message.success(editing ? 'Holiday updated' : 'Holiday added');
      qc.invalidateQueries({ queryKey: ['attendance', 'holidays'] });
      qc.invalidateQueries({ queryKey: ['attendance', 'sheet'] });
      qc.invalidateQueries({ queryKey: ['attendance', 'summary'] });
      setEditing(null);
      setCreating(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const remove = useMutation({
    mutationFn: (id: number) => attendanceApi.deleteHoliday(id),
    onSuccess: () => {
      message.success('Holiday removed');
      qc.invalidateQueries({ queryKey: ['attendance', 'holidays'] });
    },
    onError: (e) => message.error(extractError(e)),
  });

  const openCreate = () => {
    setEditing(null);
    setCreating(true);
    form.resetFields();
    form.setFieldsValue({
      holidayDate: dayjs(),
      appliesTo: 'STUDENT',
      holidayType: 'PUBLIC_HOLIDAY',
    });
  };
  const openEdit = (h: Holiday) => {
    setCreating(false);
    setEditing(h);
    form.setFieldsValue({
      holidayDate: dayjs(h.holidayDate),
      name: h.name,
      appliesTo: h.appliesTo,
      holidayType: h.holidayType,
    });
  };

  const open = creating || editing != null;

  const columns: TableProps<Holiday>['columns'] = [
    {
      title: 'Date',
      dataIndex: 'holidayDate',
      width: 140,
      render: (d: string) => dayjs(d).format('ddd, D MMM YYYY'),
      sorter: (a, b) => a.holidayDate.localeCompare(b.holidayDate),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      render: (n: string) => <Typography.Text strong>{n}</Typography.Text>,
    },
    {
      title: 'Type',
      dataIndex: 'holidayType',
      width: 160,
      render: (t: HolidayType) => <Tag color={HOLIDAY_TYPE_COLOR[t]}>{HOLIDAY_TYPE_LABEL[t]}</Tag>,
    },
    {
      title: 'Applies to',
      dataIndex: 'appliesTo',
      width: 110,
      render: (g: AttendanceGroup) => <Tag>{g}</Tag>,
    },
    {
      title: 'Actions',
      width: 130,
      align: 'right',
      render: (_, row) => (
        <Space>
          {canEdit && (
            <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(row)}>
              Edit
            </Button>
          )}
          {canDelete && (
            <Popconfirm
              title="Remove holiday?"
              okText="Remove"
              okButtonProps={{ danger: true }}
              onConfirm={() => remove.mutate(row.id)}
            >
              <Button size="small" danger type="text" icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Holidays"
      extra={
        canCreate ? (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Add holiday
          </Button>
        ) : null
      }
    >
      <Space wrap size={12} style={{ marginBottom: 16 }}>
        <DatePicker.RangePicker
          value={range}
          onChange={(r) => r && r[0] && r[1] && setRange([r[0], r[1]])}
          format="DD MMM YYYY"
          allowClear={false}
        />
        <Select
          placeholder="All groups"
          allowClear
          value={groupFilter}
          onChange={setGroupFilter}
          options={[
            { value: 'STUDENT', label: 'Student' },
            { value: 'STAFF', label: 'Staff' },
            { value: 'BOTH', label: 'Both' },
          ]}
          style={{ minWidth: 140 }}
        />
      </Space>

      {data && data.length === 0 && !isLoading ? (
        <Empty description="No holidays in this range" />
      ) : (
        <Table
          rowKey="id"
          size="middle"
          loading={isLoading}
          columns={columns}
          dataSource={data ?? []}
          pagination={false}
        />
      )}

      <Modal
        title={editing ? `Edit ${editing.name}` : 'Add holiday'}
        open={open}
        onCancel={() => {
          setEditing(null);
          setCreating(false);
        }}
        onOk={() => form.submit()}
        confirmLoading={upsert.isPending}
        okText={editing ? 'Save' : 'Add'}
        destroyOnHidden
      >
        {open && (
          <Form
            form={form}
            layout="vertical"
            onFinish={(values: HolidayFormShape) => upsert.mutate(values)}
          >
            <Form.Item label="Date" name="holidayDate" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
            </Form.Item>
            <Form.Item label="Name" name="name" rules={[{ required: true, max: 200 }]}>
              <Input autoFocus placeholder="e.g. Independence Day" />
            </Form.Item>
            <Form.Item label="Type" name="holidayType" rules={[{ required: true }]}>
              <Select
                options={(Object.keys(HOLIDAY_TYPE_LABEL) as HolidayType[]).map((t) => ({
                  value: t,
                  label: HOLIDAY_TYPE_LABEL[t],
                }))}
              />
            </Form.Item>
            <Form.Item label="Applies to" name="appliesTo" rules={[{ required: true }]}>
              <Select
                options={[
                  { value: 'STUDENT', label: 'Student only' },
                  { value: 'STAFF', label: 'Staff only' },
                  { value: 'BOTH', label: 'Both students and staff' },
                ]}
              />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </Card>
  );
}

